import React, { useEffect, useMemo, useState } from 'react'
import {
    AutoComplete,
    Button,
    Input,
    Modal,
    Select,
    Switch,
    Tooltip,
    message,
} from 'antd'
import {
    PlusOutlined,
    SaveOutlined,
} from '@ant-design/icons'
import * as com from '../../Common'
import {
    createDocumentTemplate,
    updateDocumentTemplate,
} from '../../Documents/documentTemplateApi'
import { useLoanOfficerLicenseStates } from '../../Documents/useLoanOfficerLicenseStates'
import {
    getEffectiveDocumentTemplateStates,
    resolveLicenseStateSelection,
    withAllLicenseStatesOption,
} from '../../Documents/documentTemplateStates'
import {
    DOCUMENT_TEMPLATE_INCOME_TYPE_OPTIONS,
    getDocumentTemplateIncomeTypeLabels,
    normalizeDocumentTemplateIncomeTypes,
} from '../../Documents/documentTemplateIncomeTypes'
import {
    DOCUMENT_TEMPLATE_LOAN_TYPE_OPTIONS,
    normalizeDocumentTemplateLoanTypes,
} from '../../Documents/documentTemplateLoanTypes'
import './TemplateEditorModal.css'

const CATEGORY_OPTIONS = [
    { label: 'Identification', value: 'identification' },
    { label: 'Income', value: 'income' },
    { label: 'Assets', value: 'assets' },
    { label: 'Credit', value: 'credit' },
    { label: 'Property', value: 'property' },
    { label: 'Miscellaneous', value: 'miscellaneous' },
]

const TIMEFRAME_OPTIONS = [
    { label: 'Past 30 days', value: 'recent30Days' },
    { label: 'Past 2 months', value: 'recent2Months' },
    { label: 'Past 3 months', value: 'recent3Months' },
    { label: 'Past 6 months', value: 'recent6Months' },
    { label: 'Past year', value: 'recent1Year' },
    { label: 'Past 2 years', value: 'recent2Years' },
]

const LOAN_TYPE_OPTIONS = DOCUMENT_TEMPLATE_LOAN_TYPE_OPTIONS.map(
    (option) => option.value
)
const PURPOSE_OPTIONS = ['Purchase', 'Refinance', 'Cash-out']

let rowSequence = 0
const nextRowKey = () => {
    rowSequence += 1
    return 'template-item-' + Date.now() + '-' + rowSequence
}

const createEmptyItem = () => ({
    key: nextRowKey(),
    name: '',
    category: undefined,
    timeframe: undefined,
    borrowerMessage: '',
})

const toEditorItem = (item) => ({
    key: item.id || nextRowKey(),
    name: item.name || '',
    category: item.category || undefined,
    timeframe: item.timeframe || undefined,
    borrowerMessage: item.borrowerMessage || '',
})

const getOptionLabel = (options, value) =>
    options.find((option) => option.value === value)?.label || value || ''

const initialMetadata = (template, availableStates = []) => ({
    name: template?.name || '',
    loanTypes: normalizeDocumentTemplateLoanTypes(
        template?.loanTypes || template?.loanType
    ),
    purpose: template?.purpose || '',
    incomeTypes: normalizeDocumentTemplateIncomeTypes(template?.incomeTypes),
    licenseStates: getEffectiveDocumentTemplateStates(
        template,
        availableStates
    ).filter((state) => availableStates.includes(state)),
    isDefault: Boolean(template?.isDefault),
})

const SelectionChips = ({ options, value, onChange, multiple = false }) => (
    <div className="template-metadata-chips">
        {options.map((option) => {
            const selected = multiple
                ? value.includes(option)
                : value === option
            return (
                <button
                    aria-pressed={selected}
                    className={
                        'template-metadata-chip' +
                        (selected ? ' template-metadata-chip-selected' : '')
                    }
                    key={option}
                    onClick={() => onChange(option)}
                    type="button"
                >
                    {option}
                </button>
            )
        })}
    </div>
)

const TemplateEditorModal = ({ open, template, onCancel, onSaved }) => {
    const {
        error: licenseStatesError,
        loading: licenseStatesLoading,
        options: licenseStateOptions,
        retry: retryLicenseStates,
        states: availableLicenseStates,
    } = useLoanOfficerLicenseStates(true)
    const [items, setItems] = useState([])
    const [editingItemKey, setEditingItemKey] = useState(null)
    const [itemErrors, setItemErrors] = useState({})
    const [metadata, setMetadata] = useState(initialMetadata())
    const [metadataErrors, setMetadataErrors] = useState({})
    const [metadataOpen, setMetadataOpen] = useState(false)
    const [saving, setSaving] = useState(false)

    const isEditing = Boolean(template?.id)
    const documentOptions = useMemo(
        () =>
            [...com.documentsList]
                .sort((left, right) => left.localeCompare(right))
                .map((document) => ({ label: document, value: document })),
        []
    )

    useEffect(() => {
        if (!open) {
            setMetadataOpen(false)
            setSaving(false)
            setEditingItemKey(null)
            return
        }
        setItems((template?.items || []).map(toEditorItem))
        setEditingItemKey(null)
        setItemErrors({})
        setMetadata(initialMetadata(template, availableLicenseStates))
        setMetadataErrors({})
        setMetadataOpen(false)
        setSaving(false)
    }, [availableLicenseStates, open, template])

    const updateItem = (key, field, value) => {
        setItems((currentItems) =>
            currentItems.map((item) =>
                item.key === key ? { ...item, [field]: value } : item
            )
        )
        setItemErrors((currentErrors) => ({
            ...currentErrors,
            [key]: { ...currentErrors[key], [field]: false },
        }))
    }

    const removeItem = (key) => {
        setItems((currentItems) =>
            currentItems.filter((item) => item.key !== key)
        )
        setEditingItemKey((currentKey) =>
            currentKey === key ? null : currentKey
        )
        setItemErrors((currentErrors) => {
            const nextErrors = { ...currentErrors }
            delete nextErrors[key]
            return nextErrors
        })
    }

    const validateItem = (key) => {
        const item = items.find((currentItem) => currentItem.key === key)
        if (!item) return false

        const nameMissing = !item.name.trim()
        const categoryMissing = !item.category
        const duplicateKey =
            item.name.trim().toLowerCase() + '::' + (item.timeframe || '')
        const duplicate =
            !nameMissing &&
            items.some(
                (currentItem) =>
                    currentItem.key !== key &&
                    currentItem.name.trim().toLowerCase() +
                        '::' +
                        (currentItem.timeframe || '') ===
                        duplicateKey
            )

        setItemErrors((currentErrors) => ({
            ...currentErrors,
            [key]: {
                name: nameMissing || duplicate,
                category: categoryMissing,
            },
        }))

        if (duplicate) {
            message.warning(
                'A document with the same name and time frame is already included.'
            )
        } else if (nameMissing || categoryMissing) {
            message.error('Complete the document name and category.')
        }

        return !nameMissing && !categoryMissing && !duplicate
    }

    const confirmItem = (key) => {
        if (!validateItem(key)) return false
        setEditingItemKey(null)
        return true
    }

    const editItem = (key) => {
        if (
            editingItemKey &&
            editingItemKey !== key &&
            !confirmItem(editingItemKey)
        ) {
            return
        }
        setEditingItemKey(key)
    }

    const addItem = () => {
        if (editingItemKey) return
        const item = createEmptyItem()
        setItems((currentItems) => [...currentItems, item])
        setEditingItemKey(item.key)
    }

    const validateItems = () => {
        const nextErrors = {}
        let valid = items.length > 0
        const seenItems = new Set()
        let hasDuplicate = false

        items.forEach((item) => {
            const nameMissing = !item.name.trim()
            const categoryMissing = !item.category
            if (nameMissing || categoryMissing) valid = false
            nextErrors[item.key] = {
                name: nameMissing,
                category: categoryMissing,
            }

            const duplicateKey =
                item.name.trim().toLowerCase() + '::' + (item.timeframe || '')
            if (!nameMissing && seenItems.has(duplicateKey)) {
                hasDuplicate = true
                valid = false
                nextErrors[item.key].name = true
            }
            seenItems.add(duplicateKey)
        })

        setItemErrors(nextErrors)
        if (items.length === 0) {
            message.error('Add at least one document before saving.')
        } else if (hasDuplicate) {
            message.error(
                'The template contains duplicate documents with the same time frame.'
            )
        } else if (!valid) {
            message.error('Complete the document name and category for every row.')
        }
        return valid
    }

    const openMetadata = () => {
        if (editingItemKey) return
        if (validateItems()) setMetadataOpen(true)
    }

    const toggleSingleMetadata = (field, value) => {
        setMetadata((current) => ({
            ...current,
            [field]: current[field] === value ? '' : value,
        }))
        setMetadataErrors((current) => ({ ...current, tags: false }))
    }

    const toggleMultipleMetadata = (field, value) => {
        setMetadata((current) => ({
            ...current,
            [field]: current[field].includes(value)
                ? current[field].filter((item) => item !== value)
                : [...current[field], value],
        }))
        setMetadataErrors((current) => ({ ...current, tags: false }))
    }

    const changeStates = (states) => {
        const licenseStates = resolveLicenseStateSelection(
            states,
            availableLicenseStates
        )
        setMetadata((current) => ({
            ...current,
            licenseStates,
        }))
        setMetadataErrors((current) => ({ ...current, tags: false }))
    }

    const hasAnyTag = Boolean(
        metadata.loanTypes.length ||
            metadata.purpose ||
            metadata.incomeTypes.length ||
            metadata.licenseStates.length > 0
    )
    const canBeDefault = Boolean(
        metadata.loanTypes.length &&
            metadata.purpose &&
            metadata.incomeTypes.length &&
            metadata.licenseStates.length > 0
    )
    const defaultScope = [
        metadata.loanTypes.join('/'),
        metadata.purpose,
        getDocumentTemplateIncomeTypeLabels(metadata.incomeTypes).join('/'),
        metadata.licenseStates.join('/'),
    ]
        .filter(Boolean)
        .join(' \u00b7 ')

    const saveTemplate = async () => {
        const nextErrors = {
            name: !metadata.name.trim(),
            tags: !hasAnyTag,
            defaultScope: metadata.isDefault && !canBeDefault,
        }
        setMetadataErrors(nextErrors)
        if (nextErrors.name || nextErrors.tags || nextErrors.defaultScope) return
        if (!validateItems()) {
            setMetadataOpen(false)
            return
        }

        const payload = {
            name: metadata.name.trim(),
            loanTypes: metadata.loanTypes,
            purpose: metadata.purpose,
            incomeTypes: metadata.incomeTypes,
            allStates: false,
            licenseStates: metadata.licenseStates,
            isDefault: metadata.isDefault,
            items: items.map((item, index) => ({
                name: item.name.trim(),
                category: item.category,
                timeframe: item.timeframe || '',
                borrowerMessage: item.borrowerMessage.trim(),
                ownershipLevel: 'borrower',
                borrowerId: '',
                sortOrder: index,
            })),
        }

        setSaving(true)
        try {
            const savedTemplate = isEditing
                ? await updateDocumentTemplate(template.id, payload)
                : await createDocumentTemplate(payload)
            message.success(
                '"' +
                    savedTemplate.name +
                    (isEditing ? '" updated.' : '" created.')
            )
            setMetadataOpen(false)
            await onSaved(savedTemplate)
        } catch (requestError) {
            message.error(
                requestError.message || 'Unable to save the template.'
            )
        } finally {
            setSaving(false)
        }
    }

    const closeEditor = () => {
        if (!saving) onCancel()
    }

    return (
        <>
            <Modal
                centered
                className="document-template-editor-modal"
                closable={!saving}
                footer={
                    <div className="document-template-editor-footer">
                        <Button disabled={saving} onClick={closeEditor} type="link">
                            Cancel
                        </Button>
                        <Button
                            disabled={
                                saving ||
                                Boolean(editingItemKey) ||
                                items.length === 0
                            }
                            icon={<SaveOutlined />}
                            onClick={openMetadata}
                            type="primary"
                        >
                            Save as template
                        </Button>
                    </div>
                }
                keyboard={!saving}
                maskClosable={false}
                onCancel={closeEditor}
                open={open}
                title={isEditing ? 'Edit template' : 'New template'}
                width={940}
            >
                <div className="document-template-editor-table-wrap">
                    <div className="document-template-editor-table">
                        <div className="document-template-editor-header">
                            <span>Document ({items.length})</span>
                            <span>Category</span>
                            <span>Time frame</span>
                            <span>
                                Message to borrower <small>(optional)</small>
                            </span>
                            <span />
                        </div>

                        {items.length === 0 && (
                            <div className="document-template-editor-empty">
                                No documents yet. Add rows one at a time.
                            </div>
                        )}

                        {items.map((item) => {
                            const isItemEditing = editingItemKey === item.key
                            return (
                                <div
                                    className={
                                        'document-template-editor-row' +
                                        (isItemEditing
                                            ? ' document-template-editor-row-editing'
                                            : '')
                                    }
                                    key={item.key}
                                >
                                    {isItemEditing ? (
                                        <>
                                            <AutoComplete
                                                filterOption={(inputValue, option) =>
                                                    option.value
                                                        .toUpperCase()
                                                        .includes(
                                                            inputValue.toUpperCase()
                                                        )
                                                }
                                                onChange={(value) =>
                                                    updateItem(
                                                        item.key,
                                                        'name',
                                                        value
                                                    )
                                                }
                                                options={documentOptions}
                                                placeholder="Document name"
                                                status={
                                                    itemErrors[item.key]?.name
                                                        ? 'error'
                                                        : ''
                                                }
                                                value={item.name}
                                            />
                                            <Select
                                                onChange={(value) =>
                                                    updateItem(
                                                        item.key,
                                                        'category',
                                                        value
                                                    )
                                                }
                                                options={CATEGORY_OPTIONS}
                                                placeholder="Select"
                                                status={
                                                    itemErrors[item.key]?.category
                                                        ? 'error'
                                                        : ''
                                                }
                                                value={item.category}
                                            />
                                            <Select
                                                allowClear
                                                onChange={(value) =>
                                                    updateItem(
                                                        item.key,
                                                        'timeframe',
                                                        value
                                                    )
                                                }
                                                options={TIMEFRAME_OPTIONS}
                                                placeholder="Select"
                                                value={item.timeframe}
                                            />
                                            <Input
                                                onChange={(event) =>
                                                    updateItem(
                                                        item.key,
                                                        'borrowerMessage',
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="Optional"
                                                value={item.borrowerMessage}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Tooltip
                                                placement="bottomLeft"
                                                title={item.name}
                                            >
                                                <span className="document-template-row-value">
                                                    {item.name}
                                                </span>
                                            </Tooltip>
                                            <span className="document-template-row-value">
                                                {getOptionLabel(
                                                    CATEGORY_OPTIONS,
                                                    item.category
                                                )}
                                            </span>
                                            <span className="document-template-row-value">
                                                {getOptionLabel(
                                                    TIMEFRAME_OPTIONS,
                                                    item.timeframe
                                                )}
                                            </span>
                                            <Tooltip
                                                placement="bottomLeft"
                                                title={item.borrowerMessage}
                                            >
                                                <span className="document-template-row-value">
                                                    {item.borrowerMessage}
                                                </span>
                                            </Tooltip>
                                        </>
                                    )}
                                    <div className="document-template-row-actions">
                                        <Tooltip
                                            title={
                                                isItemEditing
                                                    ? 'Confirm changes'
                                                    : 'Edit document'
                                            }
                                        >
                                            <Button
                                                aria-label={
                                                    (isItemEditing
                                                        ? 'Confirm changes to '
                                                        : 'Edit ') +
                                                    (item.name || 'document')
                                                }
                                                className="document-template-row-action"
                                                onClick={() =>
                                                    isItemEditing
                                                        ? confirmItem(item.key)
                                                        : editItem(item.key)
                                                }
                                                type="text"
                                            >
                                                <img
                                                    alt=""
                                                    src={
                                                        isItemEditing
                                                            ? '/images/document-check.svg'
                                                            : '/images/document-edit.svg'
                                                    }
                                                />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Remove document">
                                            <Button
                                                aria-label={
                                                    'Remove ' +
                                                    (item.name || 'document')
                                                }
                                                className="document-template-row-action"
                                                onClick={() => removeItem(item.key)}
                                                type="text"
                                            >
                                                <img
                                                    alt=""
                                                    src="/images/document-remove.svg"
                                                />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>
                            )
                        })}

                        <button
                            className="document-template-add-row"
                            disabled={Boolean(editingItemKey)}
                            onClick={addItem}
                            type="button"
                        >
                            <PlusOutlined /> Add more
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                centered
                className="document-template-metadata-modal"
                closable={!saving}
                footer={
                    <div className="document-template-metadata-footer">
                        <Button
                            disabled={saving}
                            onClick={() => setMetadataOpen(false)}
                            type="text"
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={saving}
                            onClick={saveTemplate}
                            type="primary"
                        >
                            {isEditing ? 'Update template' : 'Save template'}
                        </Button>
                    </div>
                }
                keyboard={!saving}
                maskClosable={false}
                onCancel={() => {
                    if (!saving) setMetadataOpen(false)
                }}
                open={metadataOpen}
                title="Save as template"
                width={520}
            >
                <div className="template-metadata-summary">
                    Saves the{' '}
                    <strong>
                        {items.length} document row{items.length === 1 ? '' : 's'}
                    </strong>{' '}
                    (name, category, time frame, message). Borrower and due date
                    are not saved.
                </div>

                <label className="template-metadata-label" htmlFor="template-name">
                    Template name
                </label>
                <Input
                    id="template-name"
                    maxLength={100}
                    onChange={(event) => {
                        setMetadata((current) => ({
                            ...current,
                            name: event.target.value,
                        }))
                        setMetadataErrors((current) => ({
                            ...current,
                            name: false,
                        }))
                    }}
                    placeholder="e.g. Conventional Purchase - W-2"
                    status={metadataErrors.name ? 'error' : ''}
                    value={metadata.name}
                />
                {metadataErrors.name && (
                    <div className="template-metadata-error">
                        Template name is required.
                    </div>
                )}

                <div className="template-metadata-grid">
                    <div>
                        <div className="template-metadata-label">Loan type</div>
                        <SelectionChips
                            multiple
                            onChange={(value) =>
                                toggleMultipleMetadata('loanTypes', value)
                            }
                            options={LOAN_TYPE_OPTIONS}
                            value={metadata.loanTypes}
                        />
                    </div>
                    <div>
                        <div className="template-metadata-label">Purpose</div>
                        <SelectionChips
                            onChange={(value) =>
                                toggleSingleMetadata('purpose', value)
                            }
                            options={PURPOSE_OPTIONS}
                            value={metadata.purpose}
                        />
                    </div>
                    <div>
                        <div className="template-metadata-label">Income type</div>
                        <Select
                            allowClear
                            className="template-metadata-state-select"
                            maxTagCount="responsive"
                            mode="multiple"
                            onChange={(incomeTypes) => {
                                setMetadata((current) => ({
                                    ...current,
                                    incomeTypes,
                                }))
                                setMetadataErrors((current) => ({
                                    ...current,
                                    tags: false,
                                }))
                            }}
                            optionFilterProp="label"
                            options={DOCUMENT_TEMPLATE_INCOME_TYPE_OPTIONS}
                            placeholder="Select income types"
                            showSearch
                            value={metadata.incomeTypes}
                        />
                    </div>
                    <div>
                        <div className="template-metadata-label">
                            License state(s) <small>your licensed states</small>
                        </div>
                        <Select
                            allowClear
                            className="template-metadata-state-select"
                            disabled={
                                licenseStatesLoading ||
                                Boolean(licenseStatesError)
                            }
                            loading={licenseStatesLoading}
                            maxTagCount="responsive"
                            mode="multiple"
                            onChange={changeStates}
                            optionFilterProp="label"
                            options={withAllLicenseStatesOption(
                                licenseStateOptions
                            )}
                            placeholder="Select licensed states"
                            showSearch
                            value={metadata.licenseStates}
                        />
                        {licenseStatesError && (
                            <div className="template-metadata-error">
                                <span>{licenseStatesError}</span>
                                <Button
                                    onClick={retryLicenseStates}
                                    size="small"
                                    type="link"
                                >
                                    Retry
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {metadataErrors.tags && (
                    <div className="template-metadata-error template-metadata-tag-error">
                        Select at least one template tag.
                    </div>
                )}

                <div className="template-metadata-default">
                    <Switch
                        checked={metadata.isDefault}
                        disabled={!metadata.isDefault && !canBeDefault}
                        onChange={(checked) => {
                            setMetadata((current) => ({
                                ...current,
                                isDefault: checked,
                            }))
                            setMetadataErrors((current) => ({
                                ...current,
                                defaultScope: false,
                            }))
                        }}
                    />
                    <div>
                        <div className="template-metadata-default-title">
                            Set as default for matching loans
                        </div>
                        <div className="template-metadata-default-description">
                            {canBeDefault
                                ? `Auto-suggested first for ${defaultScope}.`
                                : 'Requires loan type, purpose, income type, and at least one state.'}
                        </div>
                    </div>
                </div>
                {metadataErrors.defaultScope && (
                    <div className="template-metadata-error">
                        Complete all matching fields before setting a default.
                    </div>
                )}
            </Modal>
        </>
    )
}

export default TemplateEditorModal
